import type { RouterOptions } from '@nuxt/schema';
import { useSubdomain } from '~/.nuxt/imports';
import { useNuxtApp } from 'nuxt/app';

export default <RouterOptions>{
  routes: _routes => {
    const subdomain = useSubdomain();

    if (import.meta.server) {
      const { ssrContext } = useNuxtApp();
      subdomain.value = ssrContext?.event.context.subdomain || null;
    }

    // --- Scenario 1: We are on a SUBDOMAIN ('panel' or 'admin') ---
    // For developer convenience, we allow <nuxt-link> to work for all routes via redirects.
    if (subdomain.value === 'panel' || subdomain.value === 'admin') {
      const currentPrefix = `/${subdomain.value}`;

      // Part A: Get and rewrite the native routes for this subdomain.
      const nativeRoutes = _routes
        .filter(route => route.path.startsWith(currentPrefix))
        .map(route => ({
          ...route,
          path: route.path === currentPrefix ? '/' : route.path.replace(`${currentPrefix}/`, '/'),
        }));

      // Part B: Get all other "foreign" routes (from primary or other subdomains).
      const foreignRoutes = _routes.filter(route => !route.path.startsWith(currentPrefix));

      // Part C: Modify these foreign routes to become external redirects on click.
      const modifiedForeignRoutes: RouteRecordRaw[] = foreignRoutes.map(route => ({
        ...route,
        redirect: to => {
          if (import.meta.client) {
            let baseUrl = import.meta.env.VITE_SITE_URL; // Default to primary domain
            let finalPath = to.fullPath;

            if (to.path.startsWith('/admin')) {
              baseUrl = import.meta.env.VITE_ADMIN_URL;
              finalPath = to.fullPath.replace('/admin', '') || '/';
            } else if (to.path.startsWith('/panel')) {
              baseUrl = import.meta.env.VITE_PANEL_URL;
              finalPath = to.fullPath.replace('/panel', '') || '/';
            }

            const externalUrl = `${baseUrl}${finalPath}`;
            window.location.href = externalUrl;
            return false; // Cancel internal navigation
          }
          return '/'; // Server-side safeguard
        },
      }));

      // Part D: Return the combined list of routes.
      return [...nativeRoutes, ...modifiedForeignRoutes];
    } else if (subdomain.value) {
      const nativeRoutes = _routes
        .filter(route => route.path.startsWith('/@:store_slug()'))
        .map(route => ({
          ...route,
          path:
            route.path === '/@:store_slug()' ?
              route.path.replace('/@:store_slug()', '/')
            : route.path.replace('/@:store_slug()/', '/'),
        }));

      return nativeRoutes;
    }

    // --- Scenario 2: We are on the PRIMARY DOMAIN ---
    // For SEO, we use strict segregation. Only primary domain routes are available.
    else {
      // Return ONLY the routes that do NOT belong to any subdomain.
      const primaryRoutes = _routes.filter(
        route => !route.path.startsWith('/panel') && !route.path.startsWith('/admin')
      );

      return primaryRoutes;
    }
  },
  // TODO: fix
  // scrollBehavior(to, from, savedPosition) {
  //   if (savedPosition) return savedPosition;
  //
  //   if (to.hash) {
  //     const el = document.querySelector(to.hash) as HTMLElement;
  //     return { left: 0, top: (el?.offsetTop ?? 0) - 30 };
  //   }
  //
  //   if (to.fullPath === from.fullPath) return;
  //
  //   return { left: 0, top: 0, behavior: 'smooth' };
  // },
};
